<template>
  <div>
    <div
      v-for="paymentMethod in localPaymentMethods"
      :key="paymentMethod.name"
      class="payment-form-payment-option cursor-pointer border-solid border mb-5 rounded text-gray-600"
      :data-qa="`payment-method-${paymentMethod.value}`"
      :class="[isSelected(paymentMethod.paymentType) ? 'border-gray-400' : 'border-gray-200', {
        'hidden': getShouldHidePaymentOption(paymentMethod)
      }]"
      tabindex="0"
      @keydown.enter.prevent="setSelected(paymentMethod)"
      @keydown.space.prevent="setSelected(paymentMethod)"
    >
      <div
        class="flex payment-method-block justify-between items-center py-2 px-3 rounded cursor-pointer"
        :class="[`payment-method-${paymentMethod.name.toLowerCase()}`, isSelected(paymentMethod.paymentType) ? 'bg-gray-50 payment-method-block-selected' : '']"
        @click="setSelected(paymentMethod)"
      >
        <input
          type="radio"
          :name="'payment-method'"
          :value="paymentMethod.paymentType"
          :checked="isSelected(paymentMethod.paymentType)"
          class="hidden"
          @change="setSelected(paymentMethod)"
        >
        <div>
          <div class="flex items-center">
            <span class="o-body font-semibold mr-rem" v-text="paymentMethod.name.includes('.') ? $t(paymentMethod.name) : paymentMethod.name" />
            <OSvg v-for="icon in $ota.general.getPaymentIcon(paymentMethod.value)" :key="icon" :svg="icon" class="inline h-5 w-auto mr-2" />
          </div>
          <p class="text-left ota-tiny-light mt-1" v-text="getVerificationText(paymentMethod)" />
        </div>
        <div
          class="payment-method-bullet flex self-center items-center justify-center h-4 w-4 bg-gray-200 rounded-full"
          :class="isSelected(paymentMethod.paymentType) ? 'bg-gray-400' : ''"
        >
          <div
            v-if="isSelected(paymentMethod.paymentType)"
            class="h-2 w-2 bg-white rounded-full"
          />
        </div>
      </div>
      <div
        v-show="isSelected(paymentMethod.paymentType)"
        class="payment-method-accordion w-full flex-no-wrap py-5 px-6"
        :class="paddingClasses"
      >
        <div v-if="paymentMethod.value === 'paynl-ideal'" class="flex flex-wrap">
          <SignupConsent
            v-if="showConsent"
            ref="idealSignupConsent"
            :value="consentValue"
            class="pt-5 paynl-consent-container"
            :error-message="consentErrorMessage"
            @input="paymentMethodConsentChanged('idealSignupConsent', $event)"
          />
          <OptIn
            v-if="showOptIn"
            ref="idealOptIn"
            :value="optInValue"
            class="pb-5"
            :label="optinLabel"
            @input="paymentMethodOptInChanged($event)"
          />
        </div>
        <div v-if="paymentMethod.value === 'paynl-bancontact'" class="flex flex-wrap">
          <SignupConsent
            v-if="showConsent"
            ref="bancontactSignupConsent"
            :value="consentValue"
            class="pt-5 paynl-consent-container"
            :error-message="consentErrorMessage"
            @input="paymentMethodConsentChanged('bancontactSignupConsent', $event)"
          />
          <OptIn
            v-if="showOptIn"
            ref="bancontactOptIn"
            :value="optInValue"
            class="pb-5"
            :label="optinLabel"
            @input="paymentMethodOptInChanged($event)"
          />
        </div>
        <div v-else-if="paymentMethod.value === 'braintree-paypal'">
          <SignupConsent
            v-if="showConsent"
            ref="paypalSignupConsent"
            :value="consentValue"
            class="pt-5"
            :error-message="consentErrorMessage"
            @input="paymentMethodConsentChanged('paypalSignupConsent', $event)"
          />
          <OptIn
            v-if="showOptIn"
            ref="paypalOptIn"
            :value="optInValue"
            class="pb-3 pt-2"
            :label="optinLabel"
            @input="paymentMethodOptInChanged($event)"
          />
          <div
            v-if="!paymentMethodNonce"
            :id="paypalButtonId"
            :class="paypalButtonClasses"
          />
          <div v-if="paymentMethodNonce" />
        </div>
        <div v-else-if="paymentMethod.value === 'paynl-giropay'" id="paynl-giropay-container">
          <OButton
            v-if="!hideSubmit"
            size="sm"
            tag="button"
            :disabled="loading"
            @click="submit"
          >
            {{ $t('FormNavigation.Continue') }}
          </OButton>
        </div>
        <div v-else-if="paymentMethod.value === 'braintree-creditcard'" id="braintree-creditcard-creditcard-container">
          <form
            id="braintree-creditcard-form"
            class="py-5"
            @submit.prevent="submitBraintreeCreditCardForm"
          >
            <div class="card-number">
              <label for="card-number" class="braintree-creditcard-form-label ota-p14" v-text="$t('Payment.cardNumber')" />
              <div id="card-number" class="braintree-creditcard-form-input" />
            </div>

            <div class="expiration-date">
              <label for="expiration-date" class="braintree-creditcard-form-label ota-p14" v-text="$t('Payment.expirationDate')" />
              <div id="expiration-date" class="braintree-creditcard-form-input" />
            </div>

            <div class="cvv">
              <label for="cvv" class="braintree-creditcard-form-label ota-p14" v-text="$t('Payment.cvv')" />
              <div id="cvv" class="braintree-creditcard-form-input" />
            </div>

            <div class="cardholder-name">
              <label for="cardholder-name" class="braintree-creditcard-form-label ota-p14" v-text="$t('Payment.cardholderName')" />
              <div id="cardholder-name" class="braintree-creditcard-form-input" />
            </div>

            <SignupConsent
              v-if="showConsent"
              ref="creditCardSignupConsent"
              :value="consentValue"
              class="signup-consent pt-3"
              :error-message="consentErrorMessage"
              @input="paymentMethodConsentChanged('creditCardSignupConsent', $event)"
            />
            <OptIn
              v-if="showOptIn"
              ref="creditCardOptIn"
              :value="optInValue"
              class="opt-in pb-5"
              :label="optinLabel"
              @input="paymentMethodOptInChanged($event)"
            />

            <div
              :class="creditcardButtonClasses"
              @click="interceptConsentRequired($event)"
            >
              <OButton
                size="md"
                tag="button"
                type="submit"
                class="w-full"
                :disabled="loading || braintreeLoading"
              >
                {{ $t('FormNavigation.Continue') }}
              </OButton>
            </div>
          </form>
        </div>
        <div v-else-if="paymentMethod.value === 'braintree-applepay'">
          <SignupConsent
            v-if="showConsent"
            ref="applePaySignupConsent"
            :value="consentValue"
            class="pt-5"
            :error-message="consentErrorMessage"
            @input="paymentMethodConsentChanged('applePaySignupConsent', $event)"
          />
          <OptIn
            v-if="showOptIn"
            ref="applePayOptIn"
            :value="optInValue"
            class="pb-5"
            :label="optinLabel"
            @input="paymentMethodOptInChanged($event)"
          />
          <div
            v-if="!paymentMethodNonce"
            class="py-5"
          >
            <button
              :id="applePayButtonId"
              :class="applePayButtonClasses"
              type="button"
              @click="onApplePayButtonClick"
            >
              <span class="text-white font-display font-extrabold text-md" v-text="$t('SignUp.StepFive.CompleteWithConsent')" />
              <OSvg
                svg="apple-pay"
                class="w-12 h-12 fill-current text-white"
              />
            </button>
          </div>
          <div v-if="paymentMethodNonce" />
        </div>
        <div v-else-if="paymentMethod.value === 'braintree-googlepay'">
          <SignupConsent
            v-if="showConsent"
            ref="googlePaySignupConsent"
            :value="consentValue"
            class="pt-5"
            :error-message="consentErrorMessage"
            @input="paymentMethodConsentChanged('googlePaySignupConsent', $event)"
          />
          <OptIn
            v-if="showOptIn"
            ref="googlePayOptIn"
            :value="optInValue"
            class="pt-3 pb-2"
            :label="optinLabel"
            @input="paymentMethodOptInChanged($event)"
          />
          <div
            v-if="!paymentMethodNonce"
            :id="googlePayButtonId"
            :class="googlePayButtonClasses"
          />
          <div v-if="paymentMethodNonce" />
        </div>
        <div v-else>
          <OButton
            v-if="!hideSubmit"
            size="sm"
            tag="button"
            :disabled="loading"
            @click="submit"
          >
            {{ $t('FormNavigation.Continue') }}
          </OButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  IdealIssuers,
  braintreeCreditCard,
  paypal,
  BraintreeResponseTypes,
  braintreeGooglePay,
  braintreeApplePay
} from '@/enums/payments'
import PaymentMixins from '@/mixins/payments'
import { addLocalCurrency } from '@/utils/general'
import { mapState, mapActions } from 'vuex'
import { GET_BRAINTREE_CLIENT_TOKEN } from '@/utils/store/actionTypes'
import { Currencies } from '@/enums/general'
import { client as braintreeClient, googlePayment, paypalCheckout, dataCollector, threeDSecure, hostedFields, applePay } from 'braintree-web'
import { isDevelopment } from '@/enums/app'
import { datadogRum } from '@datadog/browser-rum'
import SignupConsent from '@/components/SignupConsent'
import OptIn from '@/components/OptIn'

const OButton = () => import('@/components/OButton/OButton')
const OSvg = () => import('@/components/OSvg/OSvg')

const APPLE_PAY_VERSION = 3
const OTA_MERCHANT_NAME = 'ON THAT ASS'

export default {
  name: 'PaymentForm',
  components: { OSvg, OButton, SignupConsent, OptIn },
  mixins: [PaymentMixins],
  props: {
    userCountry: {
      type: String,
      default: 'de'
    },
    hideSubmit: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideAmounts: {
      type: Boolean,
      default: false
    },
    billingAgreementDescription: {
      type: String,
      default: 'ON THAT ASS'
    },
    svg: {
      type: String,
      default: 'arrow-down'
    },
    amount: {
      type: Number,
      default: 1.00
    },
    email: {
      type: String,
      default: ''
    },
    givenName: {
      type: String,
      default: ''
    },
    surname: {
      type: String,
      default: ''
    },
    ipAddress: {
      type: String,
      default: ''
    },
    isManualPayment: {
      type: Boolean,
      default: false
    },
    consentValue: {
      type: Boolean,
      default: false
    },
    showConsent: {
      type: Boolean,
      default: false
    },
    consentErrorMessage: {
      type: String,
      default: ''
    },
    optInValue: {
      type: Boolean,
      default: false
    },
    showOptIn: {
      type: Boolean,
      default: false
    },
    optinLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      paymentMethodNonce: null,
      paypalButtonMethods: null,
      deviceData: null,
      selectedMethod: null,
      selectedIssuer: null,
      hostedFieldsInstance: null,
      braintreeLoading: true,
      threeDSecureInstance: null,
      applePayInstance: null,
      isGooglePayAvailable: false
    }
  },
  computed: {
    localPaymentMethods () {
      return this.Methods(this.userCountry, this.isManualPayment)
    },

    paddingClasses () {
      return {
        'pb-0': this.hideSubmit,
        'pt-0': this.hideSubmit && this.selectedMethod && this.selectedMethod.name !== 'ideal'
      }
    },
    idealIssuers () {
      return Object.entries(IdealIssuers).map((issuer) => {
        return {
          label: issuer[1].label,
          value: issuer[1].issuerId,
          svg: issuer[1].svg
        }
      })
    },
    paypalButtonId () {
      return 'paypal-button'
    },
    googlePayButtonId () {
      return 'google-pay-button'
    },
    applePayButtonId () {
      return 'apple-pay-button'
    },
    applePayButtonClasses () {
      const applePayButtonClasses = ['apple-pay-button', 'apple-pay-button-black', 'flex', 'flex-row', 'items-center', 'justify-center']

      if (this.showConsent && !this.consentValue) {
        applePayButtonClasses.push('braintree-button-disabled')
        applePayButtonClasses.push('applepay-button-disabled')
      }
      return applePayButtonClasses
    },
    paypalButtonClasses () {
      const paypalButtonClasses = ['py-5']

      if (this.showConsent && !this.consentValue) {
        paypalButtonClasses.push('braintree-button-disabled')
      }

      return paypalButtonClasses
    },
    googlePayButtonClasses () {
      const googlePayButtonClasses = ['py-5']

      if (this.showConsent && !this.consentValue) {
        googlePayButtonClasses.push('braintree-button-disabled')
        googlePayButtonClasses.push('googlepay-button-disabled')
      }

      return googlePayButtonClasses
    },

    creditcardButtonClasses () {
      const creditcardButtonClasses = ['card-submit', 'pt-rem']

      if (this.showConsent && !this.consentValue) {
        creditcardButtonClasses.push('braintree-button-disabled')
      }

      return creditcardButtonClasses
    },
    ...mapState('app', {
      locale: state => state.locale
    })
  },
  watch: {
    selectedMethod: {
      handler () {
        this.$emit('method', this.selectedMethod)
      },
      deep: true
    },
    selectedIssuer: {
      handler () {
        this.$emit('issuer', this.selectedIssuer)
      },
      deep: true
    },
    consentValue: {
      handler () {
        if (!this.showConsent || !this.paypalButtonMethods) {
          return
        }

        if (this.consentValue) {
          this.paypalButtonMethods.enable()
        } else {
          this.paypalButtonMethods.disable()
        }
      }
    }
  },
  async mounted () {
    await this.setupBraintree()
  },
  methods: {
    paymentMethodConsentChanged (refName, $event) {
      this.$emit('consentChange', $event)

      this.$nextTick(() => {
        if (!this.consentValue) {
          this.triggerConsentError(refName)
        }
      })
    },
    paymentMethodOptInChanged ($event) {
      this.$emit('optInChange', $event)
    },
    triggerConsentError () {
      const consentRefs = [
        'idealSignupConsent',
        'bancontactSignupConsent',
        'paypalSignupConsent',
        'creditCardSignupConsent',
        'applePaySignupConsent',
        'googlePaySignupConsent'
      ]

      for (const refName of consentRefs) {
        this.triggerSingleError(refName)
      }
    },
    triggerSingleError (refName) {
      if (this.$refs[refName]) {
        Array.isArray(this.$refs[refName]) ? this.$refs[refName][0].triggerError() : this.$refs[refName].triggerError()
      }
    },
    interceptConsentRequired (event) {
      if (!this.showConsent) {
        return
      }

      if (!this.consentValue) {
        this.triggerConsentError()
        this.$otoaster.info(this.getTranslation('SignUp.OnePage.ConsentRequired'))
        if (event) {
          event.preventDefault()
        }
      }
    },
    getTranslation (key) {
      return this.$t(`${key}`, this.variables)
    },
    getShouldHidePaymentOption (paymentMethod) {
      if (paymentMethod.value === 'braintree-applepay' && !this.applePayInstance) {
        return true
      }

      if (paymentMethod.value === 'braintree-googlepay' && !this.isGooglePayAvailable) {
        return true
      }

      return false
    },
    setSelected (method) {
      if (this.loading) {
        return
      }

      this.selectedMethod = method

      try {
        datadogRum.addAction('payment_method_selected', {
          ...method,
          amount: this.amount,
          isManualPayment: this.isManualPayment,
          country: this.userCountry,
          url: this.$route.fullPath
        })
      } catch (error) {}
    },
    async setupBraintree () {
      const clientInstance = await braintreeClient.create({
        authorization: process.env.BRAINTREE_CLIENT_TOKEN
      })

      await Promise.all([
        this.createPaypalCheckoutInstance({ clientInstance }),
        this.createBraintreeDataCollector({ clientInstance }),
        this.create3DSecureInstance({ clientInstance }),
        this.createHostedFieldsInstance({ clientInstance }),
        this.createBraintreeGooglePayCheckoutInstance({ clientInstance }),
        this.createBraintreeApplePayCheckoutInstance({ clientInstance })
      ])

      this.braintreeLoading = false
    },
    async submitBraintreeCreditCardForm () {
      this.braintreeLoading = true
      let error = this.$t('Error.general')

      try {
        const payload = await this.hostedFieldsInstance.tokenize()

        const state = this.hostedFieldsInstance.getState()
        const formValid = Object.keys(state.fields).every((key) => {
          return state.fields[key].isValid
        })

        if (formValid) {
          const threeDSecureParameters = {
            nonce: payload.nonce,
            amount: `${this.amount}`,
            bin: payload.details.bin,
            challengeRequested: true,
            email: this.email,
            collectDeviceData: true,
            billingAddress: {
              givenName: this.givenName,
              surname: this.surname
            },
            additionalInformation: {
              ipAddress: this.ipAddress
            },
            onLookupComplete: (data, next) => {
              if (data.paymentMethod.threeDSecureInfo.status === BraintreeResponseTypes.no3DSecureAvailable) {
                error = this.$t(`Form.Error.${BraintreeResponseTypes.no3DSecureAvailable}`)
              }

              next()
            }
          }

          const threeDSecurePayload = await this.threeDSecureInstance.verifyCard(threeDSecureParameters)

          if (threeDSecurePayload.liabilityShifted) { // liabilityShifted Means that the user has succesfully completed 3d secure
            this.paymentMethodNonce = threeDSecurePayload.nonce
            this.$emit('payment-nonce', threeDSecurePayload.nonce)

            this.submit()
          } else {
            this.$otoaster.danger(error, false, 10000)
          }
        } else {
          // TODO::Let the customer know their fields are invalid
          this.$otoaster.danger(this.$t('Error.general'))
        }
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }

      this.braintreeLoading = false
    },
    async createPaypalCheckoutInstance ({ clientInstance }) {
      if (!this.getHasPaymentType(paypal)) {
        return
      }

      const self = this

      try {
        const paypalCheckoutInstance = await paypalCheckout.create({
          client: clientInstance
        })

        await paypalCheckoutInstance.loadPayPalSDK({
          vault: true
        }, () => {
          (window).paypal.Buttons({
            fundingSource: (window).paypal.FUNDING.PAYPAL,
            style: {
              shape: 'pill'
            },
            onInit (_data, actions) {
              if (self.showConsent && !self.consentValue) {
                actions.disable()
              }

              self.paypalButtonMethods = actions
            },

            onClick: (_data, actions) => {
              if (!self.consentValue && self.showConsent) {
                self.interceptConsentRequired()
                return actions.reject()
              }

              return actions.resolve()
            },
            createBillingAgreement: () => {
              return paypalCheckoutInstance.createPayment({
                flow: 'vault',
                billingAgreementDescription: this.billingAgreementDescription
              })
            },
            onApprove: (data) => {
              return paypalCheckoutInstance.tokenizePayment(data, (err, payload) => {
                // Submit `payload.nonce` to your server
                if (!payload.nonce) {
                  throw err
                }

                this.paymentMethodNonce = payload.nonce
                this.$emit('payment-nonce', payload.nonce)

                this.submit()
              })
            },
            onCancel: () => { // data
              // CANCELLED
            },
            onError: (err) => {
              // eslint-disable-next-line no-console
              throw err
            }
          }).render(`#${this.paypalButtonId}`).then(function () {
          })
        })

        return paypalCheckoutInstance
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async createBraintreeGooglePayCheckoutInstance ({ clientInstance }) {
      if (!this.getHasPaymentType(braintreeGooglePay)) {
        return
      }
      const initializeGooglePay = async () => {
        const buttonContainer = document.querySelector('#google-pay-button')
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: isDevelopment ? 'TEST' : 'PRODUCTION',
          merchantInfo: {
            merchantId: process.env.GOOGLE_PAY_MERCHANT_ID,
            merchantName: OTA_MERCHANT_NAME
          }
        })

        const googlePaymentInstance = await googlePayment.create({
          client: clientInstance,
          googlePayVersion: 2,
          googleMerchantId: process.env.GOOGLE_PAY_MERCHANT_ID
        })

        const isReadyToPay = await paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods,
          existingPaymentMethodRequired: true
        })

        if (isReadyToPay.result) {
          const button = paymentsClient.createButton({
            onClick: async (event) => {
              if (this.showConsent && !this.consentValue) {
                event.preventDefault()
                return
              }

              const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest({
                transactionInfo: {
                  currencyCode: Currencies[this.userCountry],
                  totalPriceStatus: 'FINAL',
                  totalPrice: `${this.amount}`
                },
                merchantInfo: {
                  merchantId: process.env.GOOGLE_PAY_MERCHANT_ID
                },
                emailRequired: true
              })

              const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0]
              cardPaymentMethod.parameters.billingAddressRequired = true
              cardPaymentMethod.parameters.allowedAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS']
              cardPaymentMethod.parameters.allowedCardNetworks = [
                'MASTERCARD',
                'VISA'
              ]
              cardPaymentMethod.parameters.billingAddressParameters = {
                format: 'FULL',
                phoneNumberRequired: true
              }

              if (!this.isManualPayment) {
                cardPaymentMethod.parameters.allowPrepaidCards = false
              }

              try {
                const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest)
                const result = await googlePaymentInstance.parseResponse(paymentData)
                this.paymentMethodNonce = result.nonce

                this.$emit('external-identifier', paymentData.email)
                this.$emit('billing-metadata', paymentData.paymentMethodData.info)

                // payment nonce should be emitted as last, since some parent components automatically submit when this is received
                this.$emit('payment-nonce', result.nonce)

                this.submit()
              } catch (err) {
                this.$ota.general.logError(err)
                this.$otoaster.danger(err)
              }
            },
            buttonColor: 'default', // or 'black'
            buttonType: this.isManualPayment ? 'pay' : 'subscribe'
          })

          const guardConsent = (event) => {
            if (this.showConsent && !this.consentValue) {
              event.preventDefault()
              event.stopImmediatePropagation()
              this.interceptConsentRequired(event)
            }
          }

          buttonContainer.addEventListener('click', guardConsent, { capture: true })
          buttonContainer.appendChild(button)
        }
      }

      const waitForGoogle = async (retries = 2) => {
        if (window.google) {
          try {
            await initializeGooglePay()
            this.isGooglePayAvailable = true
            return
          } catch (error) {
            this.$ota.general.logError(error)
            this.$otoaster.danger(this.$t('Error.general'))
            return
          }
        }

        if (retries <= 0) {
          console.warn('Google Pay is not available - window.google not found after retries')
          return
        }

        await new Promise(resolve => setTimeout(resolve, 1000))
        return waitForGoogle(retries - 1)
      }

      try {
        await waitForGoogle()
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async createBraintreeApplePayCheckoutInstance ({ clientInstance }) {
      try {
        const isApplePayAvailableOnThisDevice = window.ApplePaySession && window.ApplePaySession.supportsVersion(APPLE_PAY_VERSION) && window.ApplePaySession.canMakePayments()

        if (!isApplePayAvailableOnThisDevice) {
          return
        }
      } catch (_) {
        return
      }

      if (!this.getHasPaymentType(braintreeApplePay)) {
        return
      }

      try {
        this.applePayInstance = await applePay.create({
          client: clientInstance
        })
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async createBraintreeDataCollector ({ clientInstance }) {
      try {
        const dataCollectorInstance = await dataCollector.create({
          client: clientInstance
        })

        this.deviceData = dataCollectorInstance.deviceData
        this.$emit('deviceData', dataCollectorInstance.deviceData)

        return dataCollectorInstance
      } catch (error) {
        this.$ota.general.logError(error)
        this.$otoaster.danger(this.$t('Error.general'))
      }
    },
    async create3DSecureInstance () {
      if (this.getHasPaymentType(braintreeCreditCard)) {
        try {
          const clientToken = await this.getBraintreeClientToken({ currency: Currencies[this.userCountry] })

          const options = {
            version: 2, // Will use 3DS2 whenever possible
            authorization: clientToken
          }

          const threeDSecureInstance = await threeDSecure.create(options)

          this.threeDSecureInstance = threeDSecureInstance

          return threeDSecureInstance
        } catch (error) {
          this.$ota.general.logError(error)
          this.$otoaster.danger(this.$t('Error.general'))
        }
      }
    },
    async createHostedFieldsInstance ({ clientInstance }) {
      if (this.getHasPaymentType(braintreeCreditCard)) {
        try {
          const options = {
            client: clientInstance,
            styles: {
              input: {
                'font-size': '16px',
                color: '#333',
                transition: 'color 160ms linear',
                '-webkit-transition': 'color 160ms linear'
              },
              'input.invalid': {
                color: '#FF7C7C'
              }
            },
            fields: {
              number: {
                container: '#card-number',
                placeholder: this.$t('Payment.cardNumberPlaceholder')
              },
              expirationDate: {
                container: '#expiration-date',
                placeholder: this.$t('Payment.expirationDatePlaceholder', { date: `10/${this.$moment().add(5, 'year').format('YYYY')}` })
              },
              cvv: {
                container: '#cvv',
                placeholder: this.$t('Payment.cvvPlaceholder')
              },
              cardholderName: {
                container: '#cardholder-name',
                placeholder: this.$t('Payment.cardholderNamePlaceholder')
              }
            }
          }

          const hostedFieldsInstance = await hostedFields.create(options)

          this.hostedFieldsInstance = hostedFieldsInstance

          return hostedFieldsInstance
        } catch (error) {
          this.$ota.general.logError(error)
        }
      }
    },
    onApplePayButtonClick () {
      if (this.showConsent && !this.consentValue) {
        this.interceptConsentRequired()
        return
      }

      try {
        if (!this.applePayInstance) {
          return
        }

        const lineItems = [
          {
            label: OTA_MERCHANT_NAME,
            amount: `${this.amount}`,
            type: 'final',
            paymentTiming: this.isManualPayment ? 'immediate' : 'recurring',
            ...(!this.isManualPayment ? {
              // recurringPaymentStartDate: new Date('2022-01-01T00:00:00'),
              recurringPaymentIntervalUnit: 'month',
              recurringPaymentIntervalCount: 1
            } : {})
          }
        ]

        const paymentRequest = this.applePayInstance.createPaymentRequest({
          total: {
            label: OTA_MERCHANT_NAME,
            amount: `${this.amount}`
          },
          countryCode: this.userCountry.toUpperCase(),
          currencyCode: Currencies[this.userCountry],
          lineItems,
          supportedNetworks: ['visa', 'masterCard'], // https://developer.apple.com/documentation/applepayontheweb/applepaypaymentrequest/supportednetworks

          // We recommend collecting billing address information, at minimum
          // billing postal code, and passing that billing postal code with
          // all Apple Pay transactions as a best practice.
          requiredBillingContactFields: ['email'],
          requiredShippingContactFields: ['email']
        })

        const session = new window.ApplePaySession(APPLE_PAY_VERSION, paymentRequest)

        session.onvalidatemerchant = (event) => {
          this.applePayInstance.performValidation({
            validationURL: event.validationURL,
            displayName: 'OnThatAss'
          }, (err, merchantSession) => {
            if (err) {
              console.error('Error validating merchant:', err)
              this.$otoaster.danger(this.$t('Error.general'))
              // You should show an error to the user, e.g. 'Apple Pay failed to load.'
              return
            }

            session.completeMerchantValidation(merchantSession)
          })
        }

        session.onpaymentauthorized = (event) => {
          this.applePayInstance.tokenize({
            token: event.payment.token
          }, (tokenizeErr, payload) => {
            if (tokenizeErr) {
              console.error('Error tokenizing Apple Pay:', tokenizeErr)
              session.completePayment(window.ApplePaySession.STATUS_FAILURE)
              return
            }

            // Send payload.nonce to your server.
            this.$emit('payment-nonce', payload.nonce)
            this.$emit('external-identifier', event.payment.shippingContact.emailAddress)
            this.paymentMethodNonce = payload.nonce

            // If requested, address information is accessible in event.payment
            // and may also be sent to your server.
            // console.log('billingPostalCode:', event.payment.billingContact.postalCode);

            this.submit()
            // After you have transacted with the payload.nonce,
            // call 'completePayment' to dismiss the Apple Pay sheet.
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
          })
        }

        session.begin()
      } catch (error) {
        console.error({ error })
        this.$ota.general.logError(error)
      }
    },
    getVerificationText (paymentMethod) {
      if (this.hideAmounts) {
        return ''
      }

      const amount = paymentMethod.verificationAmount ?? 0

      const price = addLocalCurrency(amount, `${this.locale}-${this.userCountry.toUpperCase()}`, Currencies[this.userCountry])
      const text = this.$t('Payment.verification')

      if (paymentMethod.value === braintreeCreditCard.value) {
        return `${price} ${this.$t('Payment.CreditCardVerification')}`
      }

      return `${price} ${text}`
    },
    getHasPaymentType (paymentMethod) {
      return Object.keys(this.localPaymentMethods).some(key => this.localPaymentMethods[key].value === paymentMethod.value)
    },
    isSelected (paymentType) {
      return !!this.selectedMethod && this.selectedMethod.paymentType === paymentType
    },
    selectIdealIssuer (issuer) {
      this.selectedIssuer = issuer
    },
    submit () {
      this.$emit('submit', {
        paymentMethodNonce: this.paymentMethodNonce,
        selectedMethod: this.selectedMethod,
        selectedIssuer: this.selectedIssuer,
        deviceData: this.deviceData
      })
    },
    ...mapActions('payment', {
      getBraintreeClientToken: GET_BRAINTREE_CLIENT_TOKEN
    })
  },
  head () {
    return {
      script: [
        { src: 'https://pay.google.com/gp/p/js/pay.js' }
      ]
    }
  }
}
</script>

<style scoped lang="postcss">
#braintree-creditcard-form {
  @apply grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-columns: auto;
  row-gap: 1rem;
  column-gap: 1rem;
  grid-template-areas: "card-number card-number" "expiration-date cvv" "cardholder-name cardholder-name" "signup-consent signup-consent" "opt-in opt-in" "card-submit card-submit";

  .card-number {
    grid-area: card-number;

    .braintree-creditcard-form-input {
      padding-right: 4rem;
    }
  }

  .signup-consent {
    grid-area: signup-consent;
  }

  .opt-in {
    grid-area: opt-in;
  }

  .expiration-date {
    grid-area: expiration-date;
  }

  .cvv {
    grid-area: cvv;
  }

  .cardholder-name {
    grid-area: cardholder-name;
  }

  .card-submit {
    grid-area: card-submit;
  }

  .braintree-creditcard-form-input {
    @apply bg-white rounded border-1 font-normal border-gray-600 text-gray-600 p-rem font-body;
    height: 4.2rem;
    font-size: 16px;
    line-height: 22px;
  }

  .braintree-creditcard-form-label {
    @apply text-left text-gray-600 block;
  }
}

.braintree-button-disabled {
  filter: grayscale(100%);
}

.googlepay-button-disabled, .applepay-button-disabled {
  opacity: 0.4;
}

.apple-pay-button {
  @apply bg-black py-1 px-4 rounded-md;
}

.paynl-consent-container {
  margin-bottom: 2rem;
}

@screen md {
  #braintree-creditcard-form {
    .braintree-creditcard-form-label {
      font-size: 1.4rem;
    }
  }
}

.payment-form-payment-option {
  &:focus {
    outline: 2px solid #36C0B6;
    outline-offset: -2px;
  }
}
</style>
